import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import uniqBy from 'lodash.uniqby';
import { countriesActions, accountActions } from '@actions';
import { accountService } from '@services';
import { useQueryParams } from '@hooks';
import { handleApiError } from '@helpers/api';
import { Completeness } from '@constants/companies';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import CompaniesTable from './CompaniesTable';
import EditForm from './EditForm';

const VIEW = {
    LIST: 0,
    FORM: 1,
};

const ALL_COMPLETENESS_LEVELS = [
    Completeness.Critical,
    Completeness.Low,
    Completeness.Average,
    Completeness.Good,
    Completeness.Excellent,
];
const VALID_COMPLETENESS_LEVELS = new Set(ALL_COMPLETENESS_LEVELS);

const parseCompletenessLevelsFromQuery = params => {
    const raw = params.get('completenessLevels');
    if (!raw) return ALL_COMPLETENESS_LEVELS;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return ALL_COMPLETENESS_LEVELS;
        const cleaned = Array.from(new Set(parsed.filter(v => VALID_COMPLETENESS_LEVELS.has(v))));
        return cleaned.length > 0 ? cleaned : ALL_COMPLETENESS_LEVELS;
    } catch {
        return ALL_COMPLETENESS_LEVELS;
    }
};

const getAvailableBrands = companies => [
    ...uniqBy(companies, 'brand.id')
        .filter(item => item.brand)
        .map(item => item.brand.id),
    null,
];

const reconcileBrands = (selected, previousAvailable, available) => {
    const selectedAll =
        selected.length === previousAvailable.length && previousAvailable.every(id => selected.includes(id));
    if (selectedAll) return available;
    const kept = selected.filter(id => available.includes(id));
    return kept.length ? kept : available;
};

const Companies = () => {
    const params = useQueryParams();
    const [view, setView] = useState(VIEW.LIST);
    const [selectedCompany, setSelectedCompany] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [filter, setFilter] = useState({
        brands: [],
        completenessLevels: parseCompletenessLevelsFromQuery(params),
    });

    const [detailLoading, setDetailLoading] = useState(false);

    const account = useSelector(state => state.account?.account);
    const dispatch = useDispatch();

    const writeTokenRef = useRef(0);
    const trackedOwnerRef = useRef(null);

    const closeEdit = useCallback(() => {
        writeTokenRef.current += 1;
        trackedOwnerRef.current = null;
        setDetailLoading(false);
        setSelectedCompany({});
        setView(VIEW.LIST);
    }, []);

    const fetchLocationDetail = useCallback(
        (locationId, { trackLoading = false } = {}) => {
            const requestId = ++writeTokenRef.current;
            if (trackLoading) {
                setDetailLoading(true);
                trackedOwnerRef.current = requestId;
            } else if (trackedOwnerRef.current !== null) {
                trackedOwnerRef.current = requestId;
            }
            const isOwner = () => trackedOwnerRef.current === requestId;
            accountService
                .getLocation(locationId)
                .then(detail => {
                    if (requestId !== writeTokenRef.current) return;
                    setSelectedCompany(detail);
                })
                .catch(error => {
                    if (!isOwner()) return;
                    handleApiError(error);
                    closeEdit();
                })
                .finally(() => {
                    if (!isOwner()) return;
                    trackedOwnerRef.current = null;
                    setDetailLoading(false);
                });
        },
        [closeEdit],
    );

    const openLocationEdit = useCallback(
        row => {
            if (!row?.companyId) return;
            setSelectedCompany(row);
            setView(VIEW.FORM);
            fetchLocationDetail(row.companyId, { trackLoading: true });
        },
        [fetchLocationDetail],
    );

    useEffect(() => {
        dispatch(countriesActions.get());
        dispatch(accountActions.getDegrees());
    }, []);

    const availableBrandsRef = useRef([]);
    useEffect(() => {
        const availableBrands = getAvailableBrands(account?.companies);
        const nextFilter = {
            ...filter,
            brands: reconcileBrands(filter.brands, availableBrandsRef.current, availableBrands),
        };
        availableBrandsRef.current = availableBrands;
        setFilter(nextFilter);
        dispatch(accountActions.getLocations(nextFilter, page, rowsPerPage));
    }, [account?.companies]);

    // Refetch the open location after an account refresh (integration/profile connect, etc.).
    const accountRefCount = useRef(0);
    useEffect(() => {
        accountRefCount.current += 1;
        if (accountRefCount.current <= 1) return;
        if (view === VIEW.FORM && selectedCompany?.companyId) {
            fetchLocationDetail(selectedCompany.companyId);
        }
    }, [account]);

    return (
        <>
            <CompaniesTable
                account={account}
                view={VIEW}
                setView={setView}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                openLocationEdit={openLocationEdit}
                filter={filter}
                setFilter={setFilter}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            />
            <EditForm
                isOpen={view === VIEW.FORM}
                onClose={closeEdit}
                account={account}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                loading={detailLoading}
            />
        </>
    );
};

export default Companies;
